import crypto from "node:crypto";
import fs from "node:fs";
import path from "node:path";
import { Http } from "../../utils/http";

/**
 * 视频号分片上传配置
 */
interface UploadConfig {
    chunkSize?: number; // 分片大小，默认 8MB
    concurrentLimit?: number; // 并发数，默认 4
    singleFileSize?: number; // 大文件阈值，默认 10MB
    http?: Http; // Http 实例
    timeout?: number; // 单次网络请求超时(ms)，默认 60s
    retries?: number; // 网络请求重试次数，默认 3
    retryDelay?: number; // 重试间隔(ms)，默认 2000
}

/**
 * 分片信息
 */
interface PartInfo {
    PartNumber: number;
    ETag: string;
}

/**
 * 上传进度回调
 */
type ProgressCallback = (progress: {
    uploadedChunks: number;
    totalChunks: number;
    percent: number;
    speed?: number; // KB/s
}) => void;

/**
 * 视频号分片上传器
 * 参考前端代码逻辑实现
 */
export class VideoChannelUploader {
    private chunkSize: number;
    private concurrentLimit: number;
    private singleFileSize: number;
    private http: Http;
    private reqOptions: { timeout: number; retries: number; retryDelay: number };

    // 上传状态
    private uploadTaskId: string = "";
    private partInfo: PartInfo[] = [];
    private md5: string = "";
    private chunkCount: number = 0;
    private uploadedChunks: number = 0;
    private startTime: number = 0;
    private uploadSpeed: number = 0;
    private speedRecords: number[] = [];
    private speedInterval?: NodeJS.Timeout;

    constructor(config: UploadConfig = {}) {
        this.chunkSize = config.chunkSize || 8 * 1024 * 1024; // 8MB
        this.concurrentLimit = config.concurrentLimit || 4;
        this.singleFileSize = config.singleFileSize || 10 * 1024 * 1024; // 10MB
        this.http = config.http || new Http();
        this.reqOptions = {
            timeout: config.timeout ?? 60 * 1000, // 60s，上传/下载对网络更敏感
            retries: config.retries ?? 3,
            retryDelay: config.retryDelay ?? 2000,
        };
    }

    /**
     * 计算 Buffer MD5（前 5MB）
     */
    private calculateMd5(buffer: Buffer): string {
        const MAX_SIZE = 5 * 1024 * 1024; // 5MB
        const slice = buffer.length > MAX_SIZE ? buffer.slice(0, MAX_SIZE) : buffer;
        return crypto.createHash("md5").update(slice).digest("hex");
    }

    /**
     * 计算分片 MD5（完整分片）
     */
    private calculateChunkMd5(buffer: Buffer): string {
        return crypto
            .createHash("md5")
            .update(buffer as Uint8Array)
            .digest("hex");
    }

    /**
     * 生成 UUID
     */
    private generateUUID(): string {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /**
     * 获取图片尺寸
     */
    private getImageSize(buffer: Buffer): { width: number; height: number } {
        try {
            // PNG
            if (buffer[0] === 0x89 && buffer[1] === 0x50) {
                return { width: buffer.readUInt32BE(16), height: buffer.readUInt32BE(20) };
            }
            // JPEG
            if (buffer[0] === 0xff && buffer[1] === 0xd8) {
                let offset = 2;
                while (offset < buffer.length) {
                    if (buffer[offset] !== 0xff) break;
                    const marker = buffer[offset + 1];
                    const size = buffer.readUInt16BE(offset + 2);
                    if (marker === 0xc0 || marker === 0xc1 || marker === 0xc2) {
                        return {
                            height: buffer.readUInt16BE(offset + 5),
                            width: buffer.readUInt16BE(offset + 7),
                        };
                    }
                    offset += size + 2;
                }
            }
            // GIF
            if (buffer[0] === 0x47 && buffer[1] === 0x49) {
                return { width: buffer.readUInt16LE(6), height: buffer.readUInt16LE(8) };
            }
            return { width: 0, height: 0 };
        } catch {
            return { width: 0, height: 0 };
        }
    }

    /**
     * 读取文件为 Buffer
     */
    private async readFile(filePath: string): Promise<Buffer> {
        if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
            const resp = await this.http.api<Buffer>(
                {
                    url: filePath,
                    method: "get",
                    responseType: "arraybuffer",
                },
                this.reqOptions,
            );
            return Buffer.from(resp as any);
        }
        return fs.promises.readFile(filePath);
    }

    /**
     * 获取上传认证参数（UploadID）
     */
    private async getUploadId(
        fileSize: number,
        fileName: string,
        uin: number,
        authKey: string,
        taskId: string,
    ): Promise<string> {
        const blockSum = Math.ceil(fileSize / this.chunkSize);
        const blockPartLength: number[] = [];

        // 计算每个分片的结束位置（前端逻辑）
        for (let i = 1; i <= blockSum; i++) {
            const end = Math.min(i * this.chunkSize, fileSize);
            blockPartLength.push(end);
        }

        const requestBody = {
            BlockSum: blockSum,
            BlockPartLength: blockPartLength,
        };

        const headers = {
            "X-Arguments": `apptype=251&filetype=20304&weixinnum=${uin}&filekey=${encodeURIComponent(fileName)}&filesize=${fileSize}&taskid=${taskId}&scene=2`,
            Authorization: authKey,
            Accept: "application/json, text/plain, */*",
            "Content-MD5": "null",
            Referer: "https://channels.weixin.qq.com/",
            Origin: "https://channels.weixin.qq.com/",
        };

        const resp = await this.http.api<{ UploadID?: string; errMsg?: string }>(
            {
                url: "https://finderassistancea.video.qq.com/applyuploaddfs",
                method: "put",
                headers,
                data: requestBody,
                defaultErrorMsg: "获取UploadID失败",
            },
            this.reqOptions,
        );

        if (!resp?.UploadID) {
            throw new Error(`获取 UploadID 失败: ${resp?.errMsg || "未知错误"}`);
        }

        return resp.UploadID;
    } /**
     * 上传单个分片
     */
    private async uploadChunk(
        chunk: Buffer,
        fileName: string,
        fileSize: number,
        uin: number,
        authKey: string,
        uploadId: string,
        partNumber: number,
        taskId: string,
        onProgress?: (delta: number) => void,
    ): Promise<string> {
        const md5Hash = this.calculateChunkMd5(chunk);

        const headers = {
            "Content-MD5": md5Hash,
            "X-Arguments": `apptype=251&filetype=20304&weixinnum=${uin}&filekey=${encodeURIComponent(fileName)}&filesize=${fileSize}&taskid=${taskId}&scene=0`,
            Authorization: authKey,
            "Content-Type": "application/octet-stream",
            Referer: "https://channels.weixin.qq.com/platform/post/create",
            Origin: "https://channels.weixin.qq.com",
        };

        const url = `https://finderassistancea.video.qq.com/uploadpartdfs?PartNumber=${partNumber}&UploadID=${encodeURIComponent(uploadId)}`;

        const resp = await this.http.api<{ ETag?: string; errCode?: number; errMsg?: string }>(
            {
                url,
                method: "put",
                headers,
                data: chunk,
                defaultErrorMsg: `分片 ${partNumber} 上传失败`,
            },
            this.reqOptions,
        );

        if (resp?.errCode !== undefined && resp.errCode !== 0) {
            throw new Error(`分片 ${partNumber} 上传失败: ${resp.errMsg}`);
        }

        // 简单的进度更新
        if (onProgress) {
            onProgress(chunk.length);
        }

        return resp.ETag || "";
    } /**
     * 确认分片上传（用于断点续传检查）
     */
    private async confirmChunks(
        uploadId: string,
        fileName: string,
        uin: number,
        authKey: string,
        transFlag: string,
        taskId: string,
    ): Promise<PartInfo[]> {
        const headers = {
            "Content-MD5": "null",
            "X-Arguments": `apptype=251&filetype=20304&weixinnum=${uin}&filekey=${encodeURIComponent(fileName)}&filesize=0&taskid=${taskId}&scene=0`,
            Authorization: authKey,
            Referer: "https://channels.weixin.qq.com/platform/post/create",
            Origin: "https://channels.weixin.qq.com",
        };

        const requestBody = {
            TransFlag: transFlag || "0_0",
            PartNumberMarker: "1",
        };

        const url = `https://finderassistancea.video.qq.com/listparts?UploadID=${encodeURIComponent(uploadId)}`;

        const resp = await this.http.api<{ ListPartsResult?: { Part: any[] } }>(
            {
                url,
                method: "post",
                headers,
                data: requestBody,
                defaultErrorMsg: "确认分片上传失败",
            },
            this.reqOptions,
        );

        if (resp?.ListPartsResult?.Part) {
            return resp.ListPartsResult.Part.map((p: any) => ({
                PartNumber: parseInt(p.PartNumber),
                ETag: p.ETag,
            }));
        }

        return [];
    }

    /**
     * 完成上传
     */
    private async completeUpload(
        uploadId: string,
        fileName: string,
        fileSize: number,
        uin: number,
        authKey: string,
        transFlag: string,
        partInfo: PartInfo[],
        taskId: string,
    ): Promise<string> {
        const headers = {
            "Content-MD5": "null",
            "X-Arguments": `apptype=251&filetype=20304&weixinnum=${uin}&filekey=${encodeURIComponent(fileName)}&filesize=${fileSize}&taskid=${taskId}&scene=0`,
            Authorization: authKey,
            Referer: "https://channels.weixin.qq.com/platform/post/create",
            Origin: "https://channels.weixin.qq.com",
        };

        const requestBody = {
            TransFlag: transFlag || "0_0",
            PartInfo: partInfo,
        };

        const url = `https://finderassistancea.video.qq.com/completepartuploaddfs?UploadID=${encodeURIComponent(uploadId)}`;

        const resp = await this.http.api<{ DownloadURL?: string; errMsg?: string }>(
            {
                url,
                method: "post",
                headers,
                data: requestBody,
                defaultErrorMsg: "完成上传失败",
            },
            this.reqOptions,
        );

        if (!resp?.DownloadURL) {
            throw new Error(`完成上传失败: ${resp?.errMsg || "未知错误"}`);
        }

        return resp.DownloadURL;
    }

    /**
     * 并发控制执行器
     */
    private async asyncPool<T>(concurrency: number, items: T[], fn: (item: T) => Promise<void>): Promise<void> {
        const executing: Promise<void>[] = [];

        for (const item of items) {
            const p = Promise.resolve().then(() => fn(item));
            executing.push(p);

            if (executing.length >= concurrency) {
                await Promise.race(executing);
            }
        }

        await Promise.all(executing);
    }

    /**
     * 开始上传（核心方法）
     */
    public async upload(
        imagePath: string,
        uin: number,
        authKey: string,
        options: {
            onProgress?: ProgressCallback;
            transFlag?: string;
        } = {},
    ): Promise<{
        downloadUrl: string;
        width: number;
        height: number;
        fileSize: number;
        md5: string;
    }> {
        const { onProgress, transFlag } = options;

        // 1. 读取文件
        const fileBuffer = await this.readFile(imagePath);
        const fileSize = fileBuffer.length;
        const fileName = path.basename(imagePath);

        // 检查大小限制（20MB）
        if (fileSize > 20 * 1024 * 1024) {
            throw new Error("图片大小超过 20MB 限制");
        }

        // 2. 计算 MD5 和获取尺寸
        this.md5 = this.calculateMd5(fileBuffer);
        const { width, height } = this.getImageSize(fileBuffer);

        // 3. 初始化任务
        this.startTime = Date.now();
        this.chunkCount = Math.ceil(fileSize / this.chunkSize);
        this.uploadedChunks = 0;
        this.partInfo = new Array(this.chunkCount).fill(null);

        const taskId = this.generateUUID();
        const uuid = this.generateUUID();

        console.log(`[上传开始] 文件: ${fileName}, 大小: ${fileSize}, 分片: ${this.chunkCount}`);

        // 4. 获取 UploadID
        this.uploadTaskId = await this.getUploadId(fileSize, fileName, uin, authKey, taskId);
        console.log(`[获取 UploadID] ${this.uploadTaskId}`);

        // 5. 启动速度监控
        this.startSpeedWatch();

        // 6. 分片上传
        if (fileSize <= this.chunkSize) {
            // 小文件直接上传
            console.log("[直接上传] 单文件模式");
            const etag = await this.uploadChunk(
                fileBuffer,
                fileName,
                fileSize,
                uin,
                authKey,
                this.uploadTaskId,
                1,
                taskId,
                (delta) => this.updateProgress(delta, onProgress),
            );
            this.partInfo[0] = { PartNumber: 1, ETag: etag };
            this.uploadedChunks = 1;
        } else {
            // 大文件并发分片上传
            console.log(`[分片上传] 并发数: ${this.concurrentLimit}`);

            const chunks = Array.from({ length: this.chunkCount }, (_, i) => i);

            await this.asyncPool(this.concurrentLimit, chunks, async (index) => {
                const start = index * this.chunkSize;
                const end = Math.min((index + 1) * this.chunkSize, fileSize);
                const chunk = fileBuffer.slice(start, end);

                console.log(`[上传分片 ${index + 1}/${this.chunkCount}] 大小: ${chunk.length}`);

                const etag = await this.uploadChunk(
                    chunk,
                    fileName,
                    fileSize,
                    uin,
                    authKey,
                    this.uploadTaskId,
                    index + 1,
                    taskId,
                    (delta) => this.updateProgress(delta, onProgress),
                );

                this.partInfo[index] = { PartNumber: index + 1, ETag: etag };
                this.uploadedChunks++;

                onProgress?.({
                    uploadedChunks: this.uploadedChunks,
                    totalChunks: this.chunkCount,
                    percent: Math.floor((this.uploadedChunks / this.chunkCount) * 100),
                    speed: this.uploadSpeed,
                });
            });
        }

        // 7. 确认分片（可选，用于断点续传场景）
        // const confirmedParts = await this.confirmChunks(...);

        // 8. 完成上传
        console.log("[完成上传] 合并分片...");
        const downloadUrl = await this.completeUpload(
            this.uploadTaskId,
            fileName,
            fileSize,
            uin,
            authKey,
            transFlag || "0_0",
            this.partInfo.filter(Boolean),
            taskId,
        );

        this.stopSpeedWatch();

        return {
            downloadUrl,
            width,
            height,
            fileSize,
            md5: crypto.createHash("md5").update(fileBuffer).digest("hex"),
        };
    }

    /**
     * 更新进度
     */
    private updateProgress(delta: number, onProgress?: ProgressCallback): void {
        // 计算速度的逻辑
    }

    /**
     * 启动速度监控
     */
    private startSpeedWatch(): void {
        let lastUploaded = 0;
        this.speedInterval = setInterval(() => {
            const current = this.uploadedChunks * this.chunkSize;
            this.uploadSpeed = Math.floor((current - lastUploaded) / 1024); // KB/s
            this.speedRecords.push(this.uploadSpeed);
            lastUploaded = current;
        }, 1000);
    }

    /**
     * 停止速度监控
     */
    private stopSpeedWatch(): void {
        if (this.speedInterval) {
            clearInterval(this.speedInterval);
        }
    }
}

// ==================== 使用示例 ====================

async function example() {
    const uploader = new VideoChannelUploader({
        chunkSize: 8 * 1024 * 1024, // 8MB 分片
        concurrentLimit: 4, // 4 并发
    });

    try {
        const result = await uploader.upload(
            "/path/to/large-image.jpg", // 支持 5MB-20MB 的图片
            123456789, // uin
            "your-auth-key", // authKey
            {
                onProgress: (progress) => {
                    console.log(`进度: ${progress.percent}%, 速度: ${progress.speed}KB/s`);
                },
            },
        );

        console.log("上传成功:", result.downloadUrl);
    } catch (error) {
        console.error("上传失败:", error);
    }
}
